/**
 * NetClient — Colyseus 0.15 park multiplayer (§6).
 * Przepływ: session reconnect → GET /api/park/allocate → joinById.
 * NIE używa joinOrCreate. Single active session obsługuje serwer (§6.3).
 */
import { Client, type Room } from 'colyseus.js';
import {
  EMOTE_IDS,
  GAME_HEIGHT,
  GAME_WIDTH,
  LEAVE_CODE_SESSION_TAKEN,
  type EmoteId,
  type ParkBallPublic,
  type ParkNpcPublic,
  type ParkPlayerPublic,
} from '@pieskowo/shared';
import { SaveLocal } from '../core/SaveLocal.js';
import { OutfitStore } from '../avatar/outfitStore.js';
import { serializeOutfitSlots } from '../avatar/parseOutfit.js';
import { allocateParkRoom } from './allocate.js';
import {
  classifyLeave,
  decideAfterReconnectFailure,
  type NetPhase,
} from './reconnectState.js';
import {
  clearParkSession,
  loadParkSession,
  saveParkSession,
} from './sessionStore.js';
import {
  ParkState,
  type ParkPlayerState,
  type ParkNpcState,
} from './parkSchema.js';

export type ParkSnapshot = {
  roomId: string;
  sessionId: string;
  players: ParkPlayerPublic[];
  npcs: ParkNpcPublic[];
  ball: ParkBallPublic;
};

export type NetClientListener = {
  onPhase?: (phase: NetPhase, detail?: string) => void;
  onSnapshot?: (snap: ParkSnapshot) => void;
  onNpcHearts?: (npcId: string) => void;
  onPetSmiled?: (targetId: string) => void;
  onError?: (code: string, detail?: string) => void;
};

export type NetClientOptions = {
  /** Override endpoint Colyseus (np. ws://127.0.0.1:8321). */
  endpoint?: string;
  /** Routing do pokoju znajomego (?with=). */
  withPlayerId?: string;
  listener?: NetClientListener;
  /** Wstrzyknięcie Client (testy). */
  clientFactory?: (endpoint: string) => Client;
};

const PARK_BOUNDS = {
  minX: 40,
  minY: 80,
  maxX: GAME_WIDTH - 40,
  maxY: GAME_HEIGHT - 40,
};

export function resolveColyseusEndpoint(override?: string): string {
  if (override && override.length > 0) return override;
  try {
    const env = import.meta.env as { VITE_COLYSEUS_URL?: string };
    const v = env?.VITE_COLYSEUS_URL;
    if (typeof v === 'string' && v.length > 0) return v;
  } catch {
    /* node */
  }
  // Jeden kontrakt ścieżki WSS: <proto>//<host>/colyseus (§21, P0-3).
  // colyseus.js zachowuje pathname → HTTP matchmake trafia w /colyseus/matchmake/*,
  // a WS w /colyseus/<proc>/<room>; nginx proxy `location /colyseus/` obsługuje oba.
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    const port = window.location.port;
    // Vite dev (5173) → backend bezpośrednio (bez nginx); serwer jest prefix-agnostyczny.
    if (
      (host === 'localhost' || host === '127.0.0.1') &&
      (port === '5173' || port === '')
    ) {
      return 'ws://127.0.0.1:8321/colyseus';
    }
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/colyseus`;
  }
  return 'ws://127.0.0.1:8321/colyseus';
}

export class NetClient {
  private client: Client | null = null;
  private room: Room<ParkState> | null = null;
  private phase: NetPhase = 'idle';
  private endpoint: string;
  private withPlayerId?: string;
  private listener: NetClientListener;
  private clientFactory: (endpoint: string) => Client;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectStartedAt = 0;
  private reconnectAttempt = 0;
  private disposed = false;
  private intentionalLeave = false;
  private lastSnapshot: ParkSnapshot | null = null;

  constructor(opts: NetClientOptions = {}) {
    this.endpoint = resolveColyseusEndpoint(opts.endpoint);
    this.withPlayerId = opts.withPlayerId;
    this.listener = opts.listener ?? {};
    this.clientFactory =
      opts.clientFactory ?? ((url: string) => new Client(url));
  }

  getPhase(): NetPhase {
    return this.phase;
  }

  getRoomId(): string | null {
    return this.room?.roomId ?? null;
  }

  getSessionId(): string | null {
    return this.room?.sessionId ?? null;
  }

  getSnapshot(): ParkSnapshot | null {
    return this.lastSnapshot;
  }

  getLocalPlayer(): ParkPlayerPublic | null {
    const snap = this.lastSnapshot;
    if (!snap) return null;
    return snap.players.find((p) => p.sessionId === snap.sessionId) ?? null;
  }

  /** Bounds parku (tożsame z serwerem ParkRoom). */
  static readonly BOUNDS = PARK_BOUNDS;

  /**
   * Połącz z parkiem: reconnect z sessionStorage → allocate → joinById.
   */
  async connect(): Promise<void> {
    if (this.disposed) return;
    this.intentionalLeave = false;
    this.ensureClient();

    const profileId = SaveLocal.getProfileId() ?? undefined;
    const stored = loadParkSession();
    if (
      stored &&
      (!stored.playerId || !profileId || stored.playerId === profileId)
    ) {
      const ok = await this.tryReconnect(stored.reconnectionToken);
      if (ok) return;
      clearParkSession();
    }

    await this.allocateAndJoin();
  }

  /** Świadome wyjście — sprząta token (§6.4). */
  async leave(): Promise<void> {
    this.intentionalLeave = true;
    this.clearReconnectTimer();
    clearParkSession();
    if (this.room) {
      try {
        await this.room.leave(true);
      } catch {
        /* already closed */
      }
      this.room = null;
    }
    this.setPhase('idle');
  }

  destroy(): void {
    this.disposed = true;
    this.clearReconnectTimer();
    void this.leave();
    this.client = null;
  }

  sendMoveTo(x: number, y: number): void {
    if (!this.room || this.phase !== 'connected') return;
    this.room.send('moveTo', { x, y });
  }

  sendEmote(id: EmoteId): void {
    if (!this.room || this.phase !== 'connected') return;
    if (!(EMOTE_IDS as readonly string[]).includes(id)) return;
    this.room.send('emote', { id });
  }

  sendPetNpc(npcId: string): void {
    if (!this.room || this.phase !== 'connected') return;
    if (!npcId) return;
    this.room.send('petNpc', { npcId });
  }

  sendKickBall(dirX: number, dirY: number): void {
    if (!this.room || this.phase !== 'connected') return;
    this.room.send('kickBall', { dirX, dirY });
  }

  // ── internal ──────────────────────────────────────────────────────────

  private ensureClient(): void {
    if (!this.client) {
      this.client = this.clientFactory(this.endpoint);
    }
  }

  private setPhase(phase: NetPhase, detail?: string): void {
    this.phase = phase;
    this.listener.onPhase?.(phase, detail);
  }

  private joinOptions(): Record<string, string> {
    const cache = OutfitStore.load();
    const playerId =
      SaveLocal.getProfileId() ??
      SaveLocal.getAccountId() ??
      `guest-${Date.now()}`;
    const token = SaveLocal.getToken() ?? '';
    return {
      playerId,
      dogName: cache.dogName || 'Piesek',
      breed: cache.breed || 'maltese',
      color: cache.color || '#FFF5EE',
      outfit: JSON.stringify(serializeOutfitSlots(cache.slots)),
      token,
    };
  }

  private async allocateAndJoin(): Promise<void> {
    if (this.disposed) return;
    this.setPhase('allocating');
    const alloc = await allocateParkRoom({ withPlayerId: this.withPlayerId });
    if (!alloc.ok) {
      this.setPhase(alloc.offline ? 'offline' : 'error', alloc.code);
      this.listener.onError?.(alloc.code);
      return;
    }
    await this.joinById(alloc.data.roomId);
  }

  private async joinById(roomId: string): Promise<void> {
    if (this.disposed || !this.client) return;
    this.setPhase('joining');
    try {
      const room = await this.client.joinById(
        roomId,
        this.joinOptions(),
        ParkState,
      );
      this.attachRoom(room as Room<ParkState>);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.setPhase('error', msg);
      this.listener.onError?.('JOIN_FAILED', msg);
    }
  }

  private async tryReconnect(token: string): Promise<boolean> {
    if (!this.client || this.disposed) return false;
    this.setPhase('reconnecting', 'session');
    try {
      const room = await this.client.reconnect(token, ParkState);
      this.attachRoom(room as Room<ParkState>);
      return true;
    } catch {
      return false;
    }
  }

  private attachRoom(room: Room<ParkState>): void {
    this.room = room;
    this.reconnectAttempt = 0;
    this.intentionalLeave = false;
    this.persistSession(room);
    this.setPhase('connected');

    room.onStateChange((state) => {
      this.emitSnapshot(state);
    });

    room.onMessage('npcHearts', (msg: { npcId?: string }) => {
      if (msg?.npcId) this.listener.onNpcHearts?.(msg.npcId);
    });
    room.onMessage('petSmiled', (msg: { targetId?: string }) => {
      if (msg?.targetId) this.listener.onPetSmiled?.(msg.targetId);
    });

    room.onLeave((code) => {
      void this.handleLeave(code);
    });

    room.onError((code, message) => {
      this.listener.onError?.(`ROOM_${code}`, message);
    });

    // initial snapshot
    if (room.state) {
      this.emitSnapshot(room.state);
    }
  }

  private persistSession(room: Room<ParkState>): void {
    const token = room.reconnectionToken;
    if (!token) return;
    saveParkSession({
      roomId: room.roomId,
      reconnectionToken: token,
      playerId: SaveLocal.getProfileId() ?? undefined,
    });
  }

  private async handleLeave(code: number): Promise<void> {
    const consented = this.intentionalLeave;
    const kind = classifyLeave(code, consented);
    this.room = null;

    if (kind === 'session_taken') {
      clearParkSession();
      this.setPhase('session_taken');
      return;
    }
    if (kind === 'consented' || this.intentionalLeave) {
      clearParkSession();
      this.setPhase('idle');
      return;
    }

    // disconnect flap → reconnect loop §6.4
    this.setPhase('reconnecting');
    this.reconnectStartedAt = Date.now();
    this.reconnectAttempt = 0;
    await this.runReconnectLoop();
  }

  private async runReconnectLoop(): Promise<void> {
    if (this.disposed || this.intentionalLeave) return;
    this.ensureClient();

    const stored = loadParkSession();
    if (!stored?.reconnectionToken) {
      clearParkSession();
      await this.allocateAndJoin();
      return;
    }

    const elapsed = Date.now() - this.reconnectStartedAt;
    const decision = decideAfterReconnectFailure({
      elapsedMs: elapsed,
      attempt: this.reconnectAttempt,
    });

    if (decision.action === 'stop_session_taken') {
      clearParkSession();
      this.setPhase('session_taken');
      return;
    }
    if (decision.action === 'reallocate') {
      clearParkSession();
      await this.allocateAndJoin();
      return;
    }

    // retry after backoff
    await this.sleep(decision.delayMs);
    if (this.disposed || this.intentionalLeave) return;

    try {
      const room = await this.client!.reconnect(
        stored.reconnectionToken,
        ParkState,
      );
      this.attachRoom(room as Room<ParkState>);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const sessionTaken =
        msg.includes('4001') ||
        msg.toUpperCase().includes('SESSION_TAKEN') ||
        msg.includes(String(LEAVE_CODE_SESSION_TAKEN));
      const tokenDead =
        /not found|expired|closed|invalid|does not exist/i.test(msg);

      this.reconnectAttempt += 1;
      const next = decideAfterReconnectFailure({
        elapsedMs: Date.now() - this.reconnectStartedAt,
        attempt: this.reconnectAttempt,
        sessionTaken,
        tokenDead,
      });

      if (next.action === 'stop_session_taken') {
        clearParkSession();
        this.setPhase('session_taken');
        return;
      }
      if (next.action === 'reallocate') {
        clearParkSession();
        await this.allocateAndJoin();
        return;
      }
      // continue loop
      await this.runReconnectLoop();
    }
  }

  private emitSnapshot(state: ParkState): void {
    if (!this.room) return;
    const players: ParkPlayerPublic[] = [];
    state.players.forEach((p: ParkPlayerState, sessionId: string) => {
      players.push(playerFromSchema(p, sessionId));
    });
    const npcs: ParkNpcPublic[] = [];
    state.npcs.forEach((n: ParkNpcState, npcId: string) => {
      npcs.push(npcFromSchema(n, npcId));
    });
    const ball: ParkBallPublic = {
      x: state.ball?.x ?? 640,
      y: state.ball?.y ?? 360,
      vx: state.ball?.vx ?? 0,
      vy: state.ball?.vy ?? 0,
    };
    const snap: ParkSnapshot = {
      roomId: this.room.roomId,
      sessionId: this.room.sessionId,
      players,
      npcs,
      ball,
    };
    this.lastSnapshot = snap;
    this.listener.onSnapshot?.(snap);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        resolve();
      }, ms);
    });
  }
}

function playerFromSchema(
  p: ParkPlayerState,
  sessionId: string,
): ParkPlayerPublic {
  return {
    sessionId: p.sessionId || sessionId,
    playerId: p.playerId ?? '',
    dogName: p.dogName || 'Piesek',
    breed: p.breed || 'maltese',
    color: p.color || '#FFF5EE',
    x: p.x ?? 0,
    y: p.y ?? 0,
    tx: p.tx ?? p.x ?? 0,
    ty: p.ty ?? p.y ?? 0,
    anim: (p.anim as ParkPlayerPublic['anim']) || 'idle',
    emote: (p.emote as ParkPlayerPublic['emote']) || '',
    outfit: p.outfit || '{}',
    connected: p.connected !== false,
  };
}

function npcFromSchema(n: ParkNpcState, key: string): ParkNpcPublic {
  return {
    npcId: n.npcId || key,
    x: n.x ?? 0,
    y: n.y ?? 0,
    anim: (n.anim as ParkNpcPublic['anim']) || 'idle',
    emote: (n.emote as ParkNpcPublic['emote']) || '',
  };
}

export default NetClient;
